import _ from 'lodash';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useRef } from 'react';
import themeStore from '../../../stores/themeStore';

/**
 * 트레이딩뷰 위젯 컨테이너
 */
const TvWidgetContainer = observer(
    ({ scriptSrc, configJson }: { scriptSrc: string; configJson: Record<string, any> }) => {
        const containerRef = useRef<HTMLDivElement>(null);
        const scriptRef = useRef<HTMLScriptElement | null>(null);
        const prevConfigJsonRef = useRef<Record<string, any> | null>(null);

        const scriptFullUrl = useMemo(() => {
            if (!scriptSrc) {
                return '';
            }

            const scriptRoot = 'https://s3.tradingview.com/external-embedding/';
            const filePath = scriptSrc.replace(scriptRoot, '').replace(/\\.js$/, '');
            return `${scriptRoot}${filePath}.js`;
        }, [scriptSrc]);

        const defaultConfigJson = useMemo(() => {
            return {
                locale: 'en',
                dateFormat: 'yyyy-MM-dd',
                width: '100%',
                height: '100%',
                fontFamily:
                    '-apple-system, BlinkMacSystemFont, Trebuchet MS, Roboto, Ubuntu, sans-serif',
                fontSize: '12',
                isTransparent: false,
                // 테마 따라 렌더링하게 처리
                colorTheme: themeStore.themeToRender,
                fontColor: themeStore.opacity(themeStore.colors.baseContent, 60),
                gridLineColor: themeStore.opacity(themeStore.colors.baseContent, 10),
                backgroundColor: themeStore.colors.base100,
                widgetBackgroundColor: themeStore.colors.base100,
                widgetFontColor: themeStore.colors.baseContent,
                upColor: themeStore.colors.up,
                borderUpColor: themeStore.colors.up,
                wickUpColor: themeStore.colors.up,
                volumeUpColor: themeStore.colors.up,
                downColor: themeStore.colors.down,
                borderDownColor: themeStore.colors.down,
                wickDownColor: themeStore.colors.down,
                volumeDownColor: themeStore.colors.down,
            };
        }, [themeStore.themeToRender]);

        // 설정이 바뀌더라도 중복 렌더링이 방지되도록 처되
        const mergedConfigJson = useMemo(() => {
            const merged = _.merge({}, defaultConfigJson, configJson);

            if (_.isEqual(merged, prevConfigJsonRef.current)) {
                return prevConfigJsonRef.current;
            }

            prevConfigJsonRef.current = merged;

            return merged;
        }, [defaultConfigJson, configJson]);

        useEffect(() => {
            if (!scriptFullUrl || !configJson) {
                return;
            }

            const script = document.createElement('script');
            script.src = scriptFullUrl;
            script.type = 'text/javascript';
            script.async = true;
            script.innerHTML = JSON.stringify(mergedConfigJson);
            scriptRef.current = script;

            if (containerRef.current) {
                containerRef.current.appendChild(script);
            }

            return () => {
                if (scriptRef.current) {
                    scriptRef.current.remove();
                    scriptRef.current = null;
                }

                if (containerRef.current) {
                    containerRef.current.innerHTML = '';
                }
            };
        }, [scriptFullUrl, mergedConfigJson]);

        return (
            <div className="relative h-full w-full overflow-hidden">
                <div
                    // 위젯에 기본적으로 보더가 들어가 있어서 꼼수로 외부에서 보더를 숨김
                    className="absolute -top-1 -right-1 -bottom-1 -left-1"
                >
                    <div className="tradingview-widget-container" ref={containerRef}></div>
                </div>
            </div>
        );
    },
);

export default TvWidgetContainer;
